import { useApolloClient } from '@apollo/client';
import {
  Box,
  Button,
  FieldWrapper,
  FlexBox,
  InputText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import cx from 'classnames';
import { LongRunningContext } from 'lib-knowledge-management-common/apolloClient';
import {
  KnowledgeGapJudgeDocument,
  KnowledgeGapJudgeQuery,
  KnowledgeGapJudgeQueryVariables,
} from 'lib-knowledge-management-common/graphql';
import { useModal } from 'lib-knowledge-modal';
import { SourceConversationsChip } from 'lib-knowledge-utils/components/sourceConversations/index';
import { FormEvent, useEffect, useRef, useState } from 'react';

import { mockJudgeOutcome } from './testRun.mock';
import {
  isNotFound,
  JudgeOutcome,
  TestDraft,
  KnowledgeGap,
  TestConversation,
  toTestConversations,
  toTestDrafts,
} from './testRun.utils';
import { TestRunDrafts } from './testRunDrafts';
import { GapList, TestRunOutcome } from './testRunOutcome';
import styles from './testSettingsModal.module.scss';

export type TestSettingsModalProps = {
  versionId: string | undefined;
  name: string | undefined;
};

type Step =
  | { kind: 'input'; error?: string }
  | { kind: 'running'; csid: number }
  | {
      kind: 'result';
      csid: number;
      outcome: JudgeOutcome;
      judgedAt: number;
      otherGaps: KnowledgeGap[];
      conversations: TestConversation[];
    };

type Confirm = 'close' | 'restart';

/**
 * The drafts a run returns are not stored, so closing asks first. It asks in
 * this modal: a second one would replace it, results and all.
 */
export function TestSettingsModal({
  versionId,
  name,
}: TestSettingsModalProps): JSX.Element {
  const { hideModal } = useModal();
  const client = useApolloClient();
  const [step, setStep] = useState<Step>({ kind: 'input' });
  const [csidInput, setCsidInput] = useState('');
  const [drafts, setDrafts] = useState<TestDraft[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>();
  const [confirming, setConfirming] = useState<Confirm>();
  // Ignores the answer to a run the admin walked away from.
  const runRef = useRef(0);

  const hasDrafts = step.kind === 'result' && drafts.length > 0;
  const hasSomethingToLose = step.kind === 'running' || hasDrafts;

  const restart = () => {
    runRef.current += 1;
    setDrafts([]);
    setSelectedKey(undefined);
    setConfirming(undefined);
    setStep({ kind: 'input' });
  };

  const requestClose = () => {
    if (hasSomethingToLose) setConfirming('close');
    else hideModal();
  };

  // habla's Escape handling starts fading the modal out before calling
  // `onClose`, so the question would be asked in a closing modal.
  const onEscape = useRef<() => void>(() => {});
  onEscape.current = confirming ? () => setConfirming(undefined) : requestClose;
  useEffect(() => {
    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== 'Escape' || keyEvent.defaultPrevented) return;
      if (document.querySelector('[role="menu"][aria-expanded="true"]')) return;
      onEscape.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const requestRestart = () => {
    if (hasSomethingToLose) setConfirming('restart');
    else restart();
  };

  const run = async (submitEvent?: FormEvent) => {
    submitEvent?.preventDefault();
    const csid = Number(csidInput.trim());
    if (!Number.isSafeInteger(csid) || csid <= 0) {
      setStep({ kind: 'input', error: 'Enter a conversation ID' });
      return;
    }

    const runId = ++runRef.current;
    setStep({ kind: 'running', csid });

    try {
      const mocked = await mockJudgeOutcome();
      const { data } = mocked
        ? { data: { knowledgeGapJudge: mocked } }
        : await client.query<
            KnowledgeGapJudgeQuery,
            KnowledgeGapJudgeQueryVariables
          >({
            query: KnowledgeGapJudgeDocument,
            variables: { csid, aiSettingsVersionId: versionId ?? null },
            fetchPolicy: 'no-cache',
            context: { longRunning: true } satisfies LongRunningContext,
          });
      if (runId !== runRef.current) return;

      const outcome = data.knowledgeGapJudge;
      if (!outcome) throw new Error('The test returned nothing');

      const { testDrafts, otherGaps } =
        outcome.__typename === 'KnowledgeGapJudgeOutcome_Judged'
          ? toTestDrafts(outcome)
          : { testDrafts: [], otherGaps: [] };
      setDrafts(testDrafts);
      setSelectedKey(testDrafts[0]?.key);
      setStep({
        kind: 'result',
        csid,
        outcome,
        judgedAt: Date.now(),
        otherGaps,
        conversations: toTestConversations(csid, outcome),
      });
    } catch (error) {
      if (runId !== runRef.current) return;
      setStep({
        kind: 'input',
        error: isNotFound(error)
          ? `No conversation with ID ${csid} was found.`
          : 'The test could not be run. Try again.',
      });
    }
  };

  const removeDraft = (key: string) => {
    const remaining = drafts.filter((draft) => draft.key !== key);
    if (remaining.length === 0) {
      hideModal();
      return;
    }
    const index = drafts.findIndex((draft) => draft.key === key);
    setDrafts(remaining);
    if (key === selectedKey) {
      setSelectedKey(remaining[Math.min(index, remaining.length - 1)].key);
    }
  };

  const updateDraft = (changed: TestDraft) =>
    setDrafts((current) =>
      current.map((draft) => (draft.key === changed.key ? changed : draft)),
    );

  const isWide = hasDrafts && !confirming;
  const title = versionId ? `Test "${name ?? 'version'}"` : 'Test the draft';

  return (
    <Modal
      isOpen
      allowEscClose={false}
      onClose={requestClose}
      className={cx(isWide ? styles.wide : styles.modal)}
    >
      {confirming ? (
        <ConfirmLeave
          running={step.kind === 'running'}
          restarting={confirming === 'restart'}
          onStay={() => setConfirming(undefined)}
          onLeave={confirming === 'close' ? hideModal : restart}
        />
      ) : step.kind === 'input' ? (
        <form onSubmit={(submitEvent) => void run(submitEvent)}>
          <ModalHead title={title} onClose={requestClose} />
          <ModalBody>
            <FlexBox flexDirection="column" gap="400">
              <Text.Paragraph>
                See what the AI would do when a conversation closes, using these
                settings. Nothing is saved unless you add a suggestion to drafts
                or publish it.
              </Text.Paragraph>
              <FieldWrapper
                label="Conversation ID"
                labelFor="test-settings-csid"
                meta={{ error: step.error }}
              >
                <InputText
                  id="test-settings-csid"
                  // Not `number`: habla's arrows are untyped buttons, so Enter
                  // would click the up arrow instead of submitting.
                  inputMode="numeric"
                  value={csidInput}
                  onChange={({ target }) =>
                    setCsidInput(target.value.replace(/\D/g, ''))
                  }
                  autoFocus
                />
              </FieldWrapper>
            </FlexBox>
          </ModalBody>
          <ModalFoot
            confirmName="Run test"
            confirmOnClick={() => void run()}
            confirmProps={{ disabled: !csidInput.trim() }}
            cancelOnClick={requestClose}
            cancelProps={{ variant: 'secondary' }}
          />
        </form>
      ) : step.kind === 'running' ? (
        <>
          <ModalHead title={title} onClose={requestClose} />
          <ModalBody>
            <FlexBox
              flexDirection="column"
              alignItems="center"
              gap="300"
              padding="500"
            >
              <Spinner />
              <Text.Paragraph align="center">
                {`Testing the settings on conversation ${step.csid}. This can take a few minutes.`}
              </Text.Paragraph>
            </FlexBox>
          </ModalBody>
          <ModalFoot
            cancelName="Stop test"
            cancelOnClick={requestClose}
            cancelProps={{ variant: 'secondary' }}
          />
        </>
      ) : hasDrafts ? (
        <>
          <ModalHead title={title} onClose={requestClose} />
          <FlexBox flexDirection="column" className={styles.wideBody}>
            <Box flex="0 0 auto" padding="200 500">
              <ResultIntro
                conversations={step.conversations}
                onRestart={requestRestart}
              />
            </Box>
            {step.otherGaps.length > 0 && (
              <Box
                flex="0 0 auto"
                maxHeight="30%"
                padding="0 500 400"
                className={styles.outcome}
              >
                <GapList
                  title="Gaps without a suggestion"
                  gaps={step.otherGaps}
                />
              </Box>
            )}
            <TestRunDrafts
              drafts={drafts}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onChange={updateDraft}
              onRemove={removeDraft}
              conversations={step.conversations}
              judgedAt={step.judgedAt}
            />
          </FlexBox>
        </>
      ) : (
        <>
          <ModalHead title={title} onClose={requestClose} />
          <ModalBody>
            <FlexBox flexDirection="column" gap="300">
              <ResultIntro
                conversations={step.conversations}
                onRestart={requestRestart}
              />
              <TestRunOutcome outcome={step.outcome} />
            </FlexBox>
          </ModalBody>
          <ModalFoot confirmName="Done" confirmOnClick={hideModal} />
        </>
      )}
    </Modal>
  );
}

function ResultIntro({
  conversations,
  onRestart,
}: {
  conversations: readonly TestConversation[];
  onRestart: () => void;
}) {
  const similar = conversations.length - 1;
  return (
    <FlexBox alignItems="center" justifyContent="space-between" gap="200">
      <FlexBox alignItems="center" gap="200">
        <Text.Paragraph variant="3" subdued>
          {`Tested on conversation ${conversations[0].csid}`}
          {similar > 0 &&
            ` with ${similar} similar ${similar === 1 ? 'conversation' : 'conversations'}`}
        </Text.Paragraph>
        {similar > 0 && (
          <SourceConversationsChip conversations={conversations} />
        )}
      </FlexBox>
      <Button variant="tertiary" onClick={onRestart}>
        Test another conversation
      </Button>
    </FlexBox>
  );
}

function ConfirmLeave({
  running,
  restarting,
  onStay,
  onLeave,
}: {
  running: boolean;
  restarting: boolean;
  onStay: () => void;
  onLeave: () => void;
}) {
  return (
    <>
      <ModalHead
        title={running ? 'Stop the test?' : 'Discard the test results?'}
        onClose={onStay}
      />
      <ModalBody>
        <Text.Paragraph>
          {running
            ? 'The AI is still testing the settings. If you stop now, the result is lost.'
            : 'The suggestions from this test are not saved anywhere. To keep one, add it to drafts or publish it first.'}
        </Text.Paragraph>
      </ModalBody>
      <ModalFoot
        confirmName={
          running
            ? 'Stop test'
            : restarting
              ? 'Discard and start over'
              : 'Discard results'
        }
        confirmOnClick={onLeave}
        confirmProps={{ variant: 'danger' }}
        cancelName={running ? 'Keep waiting' : 'Keep reviewing'}
        cancelOnClick={onStay}
        cancelProps={{ variant: 'secondary' }}
      />
    </>
  );
}
